import { userDataService } from './userDataService';
import { optimizationEngine } from './optimizationEngine';
import CalculationEngine from './calculationEngine';
import { LoyaltyProgram, Transaction, AIRecommendation, UserPreferences, DashboardSummary, ProgramMetrics, RedemptionOption } from '../types';

/**
 * Real data service that replaces mock data with actual user data and calculations
 * This service layer sits between components and raw data services
 */
export class RealDataService {
  
  /**
   * Get real dashboard data for authenticated users, fallback to demo data for guests
   */
  async getDashboardData(userId?: string): Promise<DashboardSummary & { 
    totalValueUSD?: number; 
    exchangeRate?: number; 
    lastUpdated?: Date; 
  }> {
    if (!userId) {
      return this.getDemoDashboardData();
    }
    
    try {
      // Get real user data
      const programs = await userDataService.getUserLoyaltyPrograms(userId);
      const transactions = await userDataService.getUserTransactions(userId);
      const preferences = await userDataService.getUserPreferences(userId);
      
      // Use enhanced calculation engine with real exchange rates
      const portfolioStats = await CalculationEngine.calculatePortfolioStats(programs);
      console.log('📊 Portfolio Statistics:', portfolioStats);
      
      // Generate real recommendations
      const topRecommendations = optimizationEngine.generateRecommendations(programs, preferences, transactions);
      
      // Get recent transactions
      const recentTransactions = transactions
        .sort((a: Transaction, b: Transaction) => b.date.getTime() - a.date.getTime())
        .slice(0, 10);
      
      // TODO: Get best redemptions from external APIs or calculated options
      const bestRedemptions: RedemptionOption[] = [];
      
      return {
        totalPoints: programs.reduce((sum: number, p: LoyaltyProgram) => sum + (p.points || 0), 0),
        totalValue: portfolioStats.totalValueINR,
        totalValueUSD: portfolioStats.totalValueUSD,
        exchangeRate: portfolioStats.exchangeRate,
        lastUpdated: portfolioStats.lastUpdated,
        pointsExpiringSoon: portfolioStats.expiringPointsValue,
        topRecommendations,
        recentTransactions,
        bestRedemptions
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return this.getDemoDashboardData();
    }
  }
  
  /**
   * Get loyalty programs for user or demo data for guests
   */
  async getLoyaltyPrograms(userId?: string): Promise<LoyaltyProgram[]> {
    if (!userId) {
      return this.getDemoPrograms();
    }
    
    try {
      return await userDataService.getUserLoyaltyPrograms(userId);
    } catch (error) {
      console.error('Error fetching loyalty programs:', error);
      return this.getDemoPrograms();
    }
  }
  
  /**
   * Get transactions for user or demo data for guests
   */
  async getTransactions(userId?: string, programId?: string): Promise<Transaction[]> {
    if (!userId) {
      return this.getDemoTransactions();
    }
    
    try {
      return await userDataService.getUserTransactions(userId, programId);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return this.getDemoTransactions();
    }
  }
  
  /**
   * Get AI recommendations for user
   */
  async getAIRecommendations(userId?: string): Promise<AIRecommendation[]> {
    if (!userId) {
      return this.getDemoRecommendations();
    }
    
    try {
      const programs = await userDataService.getUserLoyaltyPrograms(userId);
      const transactions = await userDataService.getUserTransactions(userId);
      const preferences = await userDataService.getUserPreferences(userId);
      
      return optimizationEngine.generateRecommendations(programs, preferences, transactions);
    } catch (error) {
      console.error('Error generating recommendations:', error);
      return this.getDemoRecommendations();
    }
  }
  
  /**
   * Calculate program metrics for analytics
   */
  async getProgramMetrics(userId: string, programId?: string): Promise<ProgramMetrics[]> {
    try {
      const programs = await userDataService.getUserLoyaltyPrograms(userId);
      const transactions = await userDataService.getUserTransactions(userId);
      
      const targetPrograms = programId 
        ? programs.filter((p: LoyaltyProgram) => p.id === programId)
        : programs;
      
      return targetPrograms.map((program: LoyaltyProgram) => 
        optimizationEngine.calculateProgramMetrics(program, transactions)
      );
    } catch (error) {
      console.error('Error calculating program metrics:', error);
      return [];
    }
  }
  
  /**
   * Add a new loyalty program for user
   */
  async addLoyaltyProgram(userId: string, program: Omit<LoyaltyProgram, 'id'>): Promise<string> {
    return await userDataService.addUserLoyaltyProgram(userId, program);
  }
  
  /**
   * Update a loyalty program
   */
  async updateLoyaltyProgram(programId: string, updates: Partial<LoyaltyProgram>): Promise<void> {
    return await userDataService.updateUserLoyaltyProgram(programId, updates);
  }
  
  /**
   * Delete a loyalty program
   */
  async deleteLoyaltyProgram(programId: string): Promise<void> {
    return await userDataService.deleteUserLoyaltyProgram(programId);
  }
  
  /**
   * Add a transaction
   */
  async addTransaction(userId: string, transaction: Omit<Transaction, 'id'>): Promise<string> {
    return await userDataService.addUserTransaction(userId, transaction);
  }
  
  // Demo data methods for guest users
  private getDemoDashboardData(): DashboardSummary {
    return {
      totalPoints: 387850,
      totalValue: 41653, // Updated to INR equivalent (~$470 * 88.62)
      pointsExpiringSoon: 25680,
      topRecommendations: this.getDemoRecommendations(),
      recentTransactions: this.getDemoTransactions(),
      bestRedemptions: []
    };
  }
  
  private getDemoPrograms(): LoyaltyProgram[] {
    return [
      {
        id: 'demo-united',
        name: 'United MileagePlus',
        type: 'airline',
        logo: '✈️',
        color: '#0066CC',
        points: 156750,
        tier: 'Premier Gold',
        tierColor: '#D69E2E',
        expiryDate: new Date('2025-12-31'),
        currency: 'Miles',
        conversionRate: 1.2,
        website: 'https://united.com',
        redemptionUrl: 'https://united.com/awards'
      },
      {
        id: 'demo-chase',
        name: 'Chase Sapphire Reserve',
        type: 'credit_card',
        logo: '💳',
        color: '#1A365D',
        points: 89420,
        tier: 'Premium',
        tierColor: '#553C9A',
        currency: 'Ultimate Rewards',
        conversionRate: 1.5,
        website: 'https://chase.com',
        redemptionUrl: 'https://ultimaterewards.chase.com'
      },
      {
        id: 'demo-marriott',
        name: 'Marriott Bonvoy',
        type: 'hotel',
        logo: '🏨',
        color: '#C41E3A',
        points: 141680,
        tier: 'Platinum Elite',
        tierColor: '#718096',
        expiryDate: new Date('2025-03-15'),
        currency: 'Points',
        conversionRate: 0.8,
        website: 'https://marriott.com',
        redemptionUrl: 'https://marriott.com/rewards'
      }
    ];
  }
  
  private getDemoTransactions(): Transaction[] {
    return [
      {
        id: 'demo-1',
        programId: 'demo-united',
        date: new Date('2024-11-01'),
        description: 'Flight SFO to NYC',
        points: 8450,
        type: 'earned',
        source: 'Flight',
        balance: 156750
      },
      {
        id: 'demo-2',
        programId: 'demo-chase',
        date: new Date('2024-10-28'),
        description: 'Dining purchase',
        points: 1250,
        type: 'earned',
        source: 'Credit Card',
        balance: 89420
      },
      {
        id: 'demo-3',
        programId: 'demo-marriott',
        date: new Date('2024-10-25'),
        description: 'Hotel stay - Boston',
        points: 3200,
        type: 'earned',
        source: 'Hotel Stay',
        balance: 141680
      }
    ];
  }
  
  private getDemoRecommendations(): AIRecommendation[] {
    return [
      {
        id: 'demo-rec-1',
        type: 'urgent',
        title: 'Transfer Chase points before bonus ends',
        description: 'Transfer your Ultimate Rewards to United for 30% bonus - limited time offer.',
        action: 'Transfer now',
        programsInvolved: ['demo-chase', 'demo-united'],
        pointsInvolved: 50000,
        potentialValue: 750,
        confidence: 0.9,
        priority: 'high'
      },
      {
        id: 'demo-rec-2',
        type: 'redeem',
        title: 'Book premium cabin flight',
        description: 'Great value business class redemption to Europe available.',
        action: 'Check availability',
        programsInvolved: ['demo-united'],
        pointsInvolved: 70000,
        potentialValue: 2800,
        confidence: 0.8,
        priority: 'medium'
      }
    ];
  }
}

// Export singleton instance
export const realDataService = new RealDataService();